/**
 * Notification Types
 *
 * Types for in-app notifications including budget alerts and system messages.
 *
 * @module notification
 * @created 2025-12-19 - TASK-030
 */

import { Timestamp } from 'firebase/firestore';

// ========================================
// Notification Types
// ========================================

/**
 * Notification type categories
 */
export type NotificationType =
  | 'budget_warning' // Budget threshold warning (50-90%)
  | 'budget_exceeded' // Budget exceeded (100%+)
  | 'member_joined' // New family member joined
  | 'member_left' // Family member left
  | 'rule_learning' // Rule learning suggestions available
  | 'system' // System notifications
  | 'info' // General information
  | 'reaction'; // Partner reacted to my activity (A3 리액션 피드)

/**
 * Budget alert data
 */
export interface BudgetAlertData {
  budgetAmount: number;
  actualSpending: number;
  percentage: number;
  exceededAmount?: number;
}

/**
 * In-app notification entity
 */
export interface Notification {
  id: string;
  userId: string;
  familyId?: string;
  type: NotificationType;
  title: string;
  message: string;
  threshold?: number; // For budget alerts
  yearMonth?: string; // For budget alerts
  data?: BudgetAlertData;
  read: boolean;
  createdAt: Timestamp;
}

/**
 * Firestore notification document (without id)
 */
export interface FirestoreNotification extends Omit<Notification, 'id'> {}

// ========================================
// Reaction Types (A3 리액션 피드)
// ========================================

/** 리액션으로 허용되는 이모지 */
export const REACTION_EMOJIS = ['👍', '❤️', '🎉', '🤔'] as const;
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

/**
 * 알림 항목에 대한 이모지 리액션.
 * 문서 ID는 `${notificationId}_${userId}_${emoji}` — 토글 멱등성 + rules 검증용.
 */
export interface NotificationReaction {
  id: string;
  /** 리액션을 남긴 사람 */
  userId: string;
  /** 대상 알림 문서 ID */
  notificationId: string;
  /** 대상 알림의 소유자 (파트너에게 알림 전달용) */
  notificationOwnerId: string;
  emoji: ReactionEmoji;
  createdAt: Timestamp;
}

// ========================================
// Filter Types
// ========================================

/**
 * Notification filter options
 */
export interface NotificationFilter {
  type?: NotificationType | 'all';
  read?: boolean | 'all';
  familyId?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

/**
 * Default filter settings
 */
export const DEFAULT_NOTIFICATION_FILTER: NotificationFilter = {
  type: 'all',
  read: 'all',
};

// ========================================
// Notification Display Configs
// ========================================

/**
 * Notification type display config
 */
export interface NotificationTypeConfig {
  type: NotificationType;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}

/**
 * Notification type configurations
 */
export const NOTIFICATION_TYPE_CONFIGS: Record<NotificationType, NotificationTypeConfig> = {
  budget_warning: {
    type: 'budget_warning',
    label: '예산 경고',
    icon: '⚠️',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  budget_exceeded: {
    type: 'budget_exceeded',
    label: '예산 초과',
    icon: '🚨',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  reaction: {
    type: 'reaction',
    label: '파트너 반응',
    icon: '💬',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  member_joined: {
    type: 'member_joined',
    label: '멤버 가입',
    icon: '👋',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  member_left: {
    type: 'member_left',
    label: '멤버 탈퇴',
    icon: '👤',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
  },
  rule_learning: {
    type: 'rule_learning',
    label: '규칙 학습',
    icon: '🧠',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  system: {
    type: 'system',
    label: '시스템',
    icon: '⚙️',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  info: {
    type: 'info',
    label: '안내',
    icon: 'ℹ️',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
};

// ========================================
// Utility Functions
// ========================================

/**
 * Get notification type config
 */
export function getNotificationTypeConfig(type: NotificationType): NotificationTypeConfig {
  return NOTIFICATION_TYPE_CONFIGS[type] || NOTIFICATION_TYPE_CONFIGS.info;
}

/**
 * Check if notification is budget-related
 */
export function isBudgetNotification(type: NotificationType): boolean {
  return type === 'budget_warning' || type === 'budget_exceeded';
}

/**
 * Format notification relative time
 */
export function formatNotificationTime(timestamp: Timestamp): string {
  const date = timestamp.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;

  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
